<script lang="ts">
	import { format } from 'svelte-i18n';
	import {
		Alert,
		Button,
		Checkbox,
		Container,
		Modal,
		Text,
		Toggle,
	} from '@Shelly/shelly-design-system';
	import { compareFWVersions } from 'src/helpers/firmware_compare_helpers';
	import {
		getBluTRVGatewayFirmwareVersion,
		getTRVFirmwareVersion,
	} from 'src/helpers/firmware_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { callBluTRV, getTrvComponentByDevice } from 'src/helpers/virtual_helpers/blutrv_helpers';
	import type { Device } from 'src/types/Device';
	import type { CloudApiResponse } from 'src/types/responses/CloudApiResponse';
	import TransitionWrapper from 'src/components/Utility/TransitionWrapper.svelte';

	export let device: Device;
	let waitForResponse = false;
	let waitForEnableResponse = false;

	type Flags =
		| 'floor_heating'
		| 'accel'
		| 'force_close'
		| 'extra_pressure'
		| 'auto_calibrate'
		| 'anticlog'
		| 'power_save'
		| 'silent_mode';

	type EnabledFlags = Partial<Array<Flags>>;

	let enabledFlags: EnabledFlags = getTrvComponentByDevice(device)?.config?.['trv:0']?.flags || [];
	let enableTrv = getTrvComponentByDevice(device)?.config?.['trv:0']?.enable;

	const isPowerBalancedHeatingSupport = compareFWVersions(
		getTRVFirmwareVersion(device),
		'>=',
		'1.2.0',
	);
	const isSilentModeSupport =
		compareFWVersions(getTRVFirmwareVersion(device), '>=', '1.3.0') &&
		compareFWVersions(getBluTRVGatewayFirmwareVersion(device), '>=', '2.0.0');
	const powerSaveKey = 'power_save';
	let init_power_save = enabledFlags.includes(powerSaveKey);
	let showBalancedHeatingPrompt = false;

	const handleEnableTrv = async () => {
		waitForEnableResponse = true;
		enableTrv = !enableTrv;
		await callBluTRV(device, 'Trv.Setconfig', {
			id: 0,
			config: { enable: enableTrv },
		});
		waitForEnableResponse = false;
	};

	const handleBehaviour = async (flag: Flags): Promise<void> => {
		if (enabledFlags.includes(flag)) {
			enabledFlags = enabledFlags.filter((item) => item !== flag);
		} else {
			enabledFlags = [...enabledFlags, flag];
		}
	};

	const behaviour = async (): Promise<void> => {
		waitForResponse = true;
		const res: CloudApiResponse = await callBluTRV(device, 'Trv.Setconfig', {
			id: 0,
			config: { flags: enabledFlags },
		});
		if (res.isok) {
			init_power_save = enabledFlags.includes(powerSaveKey);
		}
		waitForResponse = false;
	};
</script>

<Alert
	class="mb-s"
	type="warning"
	heading={capFirstChar(
		$format('alert_warning_generic_attention', {
			default: 'Attention!',
		}),
	)}
	dismissable
	isHeadingBold
	message={$format('blutrv_enable_warning_message_description', {
		default:
			'If disabled, the TRV would not open/close the valve automatically, which is suitable only if using advanced API calls to control the valve position manually.',
	})}
/>

<Toggle
	loading={waitForEnableResponse}
	disabled={waitForEnableResponse}
	checked={enableTrv}
	size="m"
	class="mb-m"
	on:click={handleEnableTrv}
	id="toggle-trv-enable"
>
	{$format('trv_auto_description', {
		default: 'Enable automatic temperature control',
	})}
</Toggle>

{#if !enableTrv}
	<!-- TODO: Add translations for alert_info_heading_trv_behavior_disabled and alert_info_message_trv_behavior_disabled to all language files -->
	<TransitionWrapper>
		<Alert
			class="mb-s"
			type="info"
			heading={capFirstChar(
				$format('alert_info_heading_trv_behavior_disabled', {
					default: 'Behavior settings disabled',
				}),
			)}
			isHeadingBold
			message={$format('alert_info_message_trv_behavior_disabled', {
				default:
					'The behavior settings below have no effect while automatic temperature control is disabled. Enable automatic temperature control to configure these settings.',
			})}
		/>
	</TransitionWrapper>
{/if}

<Checkbox
	size="m"
	class="flex-shrink-0 mr-s mb-s"
	checked={enabledFlags.includes('floor_heating')}
	disabled={!enableTrv}
	info={$format('checkbox_info_blu_trv_behavior_floor_heating', {
		default: 'Only data from the external sensor is used, ignoring the built-in sensor',
	})}
	on:click={() => handleBehaviour('floor_heating')}
	id="checkbox-at4icyn"
>
	{$format('checkbox_blu_trv_behavior_floor_heating', {
		default: 'Underfloor heating',
	})}
</Checkbox>
<Checkbox
	size="m"
	class="flex-shrink-0 mr-s mb-s"
	checked={enabledFlags.includes('accel')}
	disabled={!enableTrv}
	info={$format('checkbox_info_blu_trv_behavior_accel', {
		default: 'Reduce the time needed to reach the target temperature',
	})}
	on:click={() => handleBehaviour('accel')}
	id="checkbox-yhe15nj"
>
	{$format('checkbox_blu_trv_behavior_accel', {
		default: 'Accelerated heating',
	})}
</Checkbox>
<Checkbox
	size="m"
	class="flex-shrink-0 mr-s mb-s"
	checked={enabledFlags.includes('auto_calibrate')}
	disabled={!enableTrv}
	info={$format('checkbox_info_blu_trv_behavior_auto_calibrate', {
		default: 'The TRV will automatically correct the calibration if needed',
	})}
	on:click={() => handleBehaviour('auto_calibrate')}
	id="checkbox-6fcmvz4"
>
	{$format('checkbox_blu_trv_behavior_auto_calibrate', {
		default: 'Auto calibration',
	})}
</Checkbox>
<Checkbox
	size="m"
	class="flex-shrink-0 mr-s mb-s"
	checked={enabledFlags.includes('anticlog')}
	disabled={!enableTrv}
	info={$format('checkbox_info_blu_trv_behavior_anticlog', {
		default: 'If enabled, the TRV will ensure at least one movement per week to prevent clogging',
	})}
	on:click={() => handleBehaviour('anticlog')}
	id="checkbox-2nvyipx"
>
	{$format('checkbox_blu_trv_behavior_anticlog', {
		default: 'Clog prevention',
	})}
</Checkbox>
{#if isPowerBalancedHeatingSupport}
	<Checkbox
		size="m"
		class="flex-shrink-0 mr-s mb-s"
		checked={enabledFlags.includes(powerSaveKey)}
		disabled={!enableTrv}
		info={$format('checkbox_info_blu_trv_balanced_heating', {
			default: 'If enabled, balances power-saving and precision; otherwise, favors precision.',
		})}
		on:click={() => handleBehaviour(powerSaveKey)}
		id="checkbox-o84r4v0"
	>
		{$format('checkbox_blu_trv_balanced_heating', {
			default: 'Balanced heating',
		})}
	</Checkbox>
{/if}
{#if isSilentModeSupport}
	<Checkbox
		size="m"
		class="flex-shrink-0 mr-s mb-s"
		checked={enabledFlags.includes('silent_mode')}
		disabled={!enableTrv}
		info={$format('checkbox_info_blu_trv_behavior_silent_mode', {
			default: 'Reduce noise during valve adjustments.',
		})}
		on:click={() => handleBehaviour('silent_mode')}
		id="checkbox-blu-trv-silent-mode"
	>
		{$format('checkbox_blu_trv_behavior_silent_mode', {
			default: 'Silent mode',
		})}
	</Checkbox>
{/if}

<Button
	loading={waitForResponse}
	disabled={waitForResponse || !enableTrv}
	class="mt-s"
	size="m"
	width="full"
	on:click={() => {
		if (isPowerBalancedHeatingSupport && enabledFlags.includes(powerSaveKey) != init_power_save) {
			showBalancedHeatingPrompt = true;
		} else {
			behaviour();
		}
	}}
	>{$format('button_save_settings', {
		default: 'Save settings',
	})}</Button
>

<Modal
	on:close={() => {
		showBalancedHeatingPrompt = false;
	}}
	showModal={showBalancedHeatingPrompt}
	title={$format('label_exit_modal', {
		default: 'Exit Without Saving?',
	})}
	backdropClose={false}
>
	<Container slot="content" class="p-s" padding="0">
		<Text>
			{$format('blu_trv_prompt_balanced_heating_info', {
				default:
					'Toggling Balanced heating resets algorithm settings and the BLU TRV may take some time to adapt to your heating patterns. Apply the change?',
			})}
		</Text>
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			width="m"
			variant="secondary"
			class="mr-s"
			on:click={() => {
				showBalancedHeatingPrompt = false;
			}}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			width="m"
			on:click={() => {
				showBalancedHeatingPrompt = false;
				behaviour();
			}}
		>
			{capFirstChar($format('button_apply', { default: 'Apply' }))}
		</Button>
	</div>
</Modal>
